import moment from 'moment';
import { Route } from 'vue-router';

import { PaymentState } from '@/enums/paymentState';
import { PublicSettings } from '@/enums/publicSettings.enum';
import { TableName } from '@/enums/tableName';
import { PaymentPack } from '@/interfaces/paymentPack';
import { StructureNode } from '@/interfaces/structureNode';
import store from '@/store/store';
import { tryParseInt } from './number';
import { prepareQuery } from './prepareQuery';

/**
 * Pobiera szczegóły paczki płatności
 *
 * @export
 * @param {Route} route
 * @returns {Promise<any>}
 */
export function getPaymentPackDetails(route: Route): Promise<any> {
  const entityType = TableName.procedure;
  const entityId = route.params.procedureId.toString();
  const packId = tryParseInt(route.params.packId) as number;
  return Promise.all([
    getPaymentPack(packId).then(pack => (route.meta.breadcrumb.title = pack.number)),
    store.dispatch('payment/list', {
      ...prepareQuery(route),
      count: -1,
      start: 0,
      packId,
      structureIds: store.state.auth.nodes.map((node: any) => node.id) // permission related - school payment operator
    }),
    store
      .dispatch('setting/findPublic', {
        code: PublicSettings.CHILD_PROCEDURE_ID,
        entityType,
        entityId
      })
      .then(setting =>
        Promise.all([
          store.dispatch('setting/findPublic', {
            code: PublicSettings.PAYMENT_NO_SPLIT,
            entityId: setting?.value || entityId,
            entityType
          }),
          store.dispatch('setting/findPublic', {
            code: PublicSettings.PAYMENT_COLUMNS,
            entityId: setting?.value || entityId,
            entityType
          })
        ])
      )
  ]);
}

/**
 * Pobiera listę płatności
 *
 * @export
 * @param {number} packId
 * @returns {Promise<PaymentPack>}
 */
export function getPaymentPack(packId: number): Promise<PaymentPack> {
  return store
    .dispatch('paymentPack/list', {
      ids: [packId],
      start: 0,
      count: 1,
      structureIds: store.state.auth.nodes.map((node: any) => node.id)
    }) // permission related - payment operator
    .then(response => response.list[0]);
}

/**
 * Pobiera listę wypłat
 *
 * @export
 * @param {Route} route
 * @param {Date} [paymentDate=new Date()]
 * @returns {Promise<any>}
 */
export function getPaymentList(route: Route, paymentDate: Date = new Date()): Promise<any> {
  const entityType = TableName.procedure;
  let entityId = route.params.procedureId.toString();
  return store
    .dispatch('setting/findPublic', {
      code: PublicSettings.CHILD_PROCEDURE_ID,
      entityType: TableName.procedure,
      entityId: route.params.procedureId.toString()
    })
    .then(setting => {
      if (setting?.value) {
        entityId = setting.value;
      }

      return store.dispatch('setting/findPublic', { code: PublicSettings.PAYMENT_LIST_TYPES, entityType, entityId });
    })
    .then(() => preparePaymentListPermissions())
    .then(() => getPaymentListVariables(route, tryParseInt(entityId)))
    .then(data => {
      const types =
        store.state.setting.settings[PublicSettings.PAYMENT_LIST_TYPES]?.value &&
        JSON.parse(store.state.setting.settings[PublicSettings.PAYMENT_LIST_TYPES].value);
      const selectedPaymentListTypeIndex = tryParseInt(route?.query.selectedType, 0) as number;
      const paymentListType = types?.[selectedPaymentListTypeIndex];
      return Promise.all([
        store.dispatch('procedure/paymentList', data),
        store.dispatch('setting/findPublic', { code: PublicSettings.PAYMENT_COLUMNS, entityType, entityId }),
        store
          .dispatch('setting/findPublic', { code: PublicSettings.KSAT_DISABLED, entityType, entityId })
          .then(setting => {
            if (setting?.value === 'true') {
              return;
            }
            return store.dispatch('paymentPack/list', {
              order: 'number',
              desc: false,
              procedureId: tryParseInt(entityId),
              statuses: [PaymentState.NOT_SENT, PaymentState.UNCONFIRMED],
              structureIds: store.state.auth.nodes.map((node: any) => node.id),
              types: paymentListType ? [paymentListType.name] : undefined
            });
          }),
        store
          .dispatch('setting/findPublic', { code: PublicSettings.PAYMENT_NO_SPLIT, entityType, entityId })
          .then(setting => setting?.value === 'true')
          .then(noSplit => {
            if (noSplit) {
              return;
            }
            return store.dispatch('procedure/paymentListSplit', {
              ...data,
              paymentDate: route.query.paymentDate || paymentDate,
              paymentType: paymentListType?.name
            });
          })
      ]);
    });
}

/**
 * Zwraca dane zapytania o listę wypłat
 *
 * @export
 * @param {Route} route
 * @returns {Promise<any>}
 */
export function getPaymentListVariables(route: Route, procedureId?: number): Promise<any> {
  const data: any = { date: getPaymentListDate(route) };

  if (procedureId) {
    data.id = procedureId;
  }

  if (route.query.selectedSchool) {
    data.structureIds = [tryParseInt(route.query.selectedSchool)];
  } else if (store.getters['auth/paymentListStructures'].length) {
    const structureIds = store.getters['auth/paymentListStructures'].map((node: any) => node.id);
    data.structureIds = [structureIds[0]];
  }

  if (store.state.setting.settings[PublicSettings.PAYMENT_LIST_TYPES]?.value) {
    const types = JSON.parse(store.state.setting.settings[PublicSettings.PAYMENT_LIST_TYPES].value);
    const index = tryParseInt(route.query.selectedType) || 0;
    data.extraFilters = JSON.parse(types[index].value);
  }

  return data;
}

/**
 * Pobiera datę listy wypłat
 *
 * @param {Route} route
 * @returns {Date}
 */
export function getPaymentListDate(route: Route): Date {
  const { year, month } = route.query;

  if (year === undefined || month === undefined) {
    return moment()
      .add(1, 'month')
      .startOf('month')
      .toDate();
  }

  return moment()
    .year(tryParseInt(year) as number)
    .month((tryParseInt(month) as number) + 1)
    .startOf('month')
    .toDate();
}

/**
 * Przygotowuje uprawnienia do listy wypłat
 *
 * @returns {Promise<any>}
 */
function preparePaymentListPermissions(): Promise<any> {
  const structures: StructureNode[] = store.getters['auth/paymentListAvailableStructures'];
  if (structures.length) {
    const permissions = structures.map(structure => `PAYMENT.LIST_SPECIAL:${TableName.structure_node}:${structure.id}`);
    return store.dispatch('auth/checkPermissionsSync', permissions);
  }
  return Promise.resolve(undefined);
}
